if (!customElements.get('pdp-slider-component')) {
  class PDPProductSlider extends SliderComponent {
    constructor() {
      super();
      if (!this.slider) return;

      this.loopMediaQuery = window.matchMedia('(max-width: 999px)');
      this.loopSetupComplete = true;
      this.loopActive = false;
      this.isJumping = false;
      this.loopResetTimer = null;
      this.loopRebuildFrame = null;
      this.supportsScrollEnd = 'onscrollend' in this.slider;
      this.onLoopScrollEnd = this.resetLoopBoundary.bind(this);
      if (this.supportsScrollEnd) this.slider.addEventListener('scrollend', this.onLoopScrollEnd);

      this.onLoopBreakpointChange = this.scheduleLoopRebuild.bind(this);
      if (this.loopMediaQuery.addEventListener) {
        this.loopMediaQuery.addEventListener('change', this.onLoopBreakpointChange);
      } else {
        this.loopMediaQuery.addListener(this.onLoopBreakpointChange);
      }

      this.loopMutationObserver = new MutationObserver((mutations) => {
        const realSlidesChanged = mutations.some((mutation) => {
          return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
            return node.nodeType === Node.ELEMENT_NODE && node.matches('[id^="Slide-"]');
          });
        });

        if (realSlidesChanged) this.scheduleLoopRebuild();
      });
      this.loopMutationObserver.observe(this.slider, { childList: true });

      this.scheduleLoopRebuild();
    }

    disconnectedCallback() {
      this.loopMutationObserver?.disconnect();
      window.clearTimeout(this.loopResetTimer);
      window.cancelAnimationFrame(this.loopRebuildFrame);
      if (this.supportsScrollEnd) this.slider.removeEventListener('scrollend', this.onLoopScrollEnd);

      if (!this.loopMediaQuery) return;
      if (this.loopMediaQuery.removeEventListener) {
        this.loopMediaQuery.removeEventListener('change', this.onLoopBreakpointChange);
      } else {
        this.loopMediaQuery.removeListener(this.onLoopBreakpointChange);
      }
    }

    initPages() {
      if (!this.loopSetupComplete) {
        super.initPages();
        return;
      }

      this.scheduleLoopRebuild();
    }

    resetPages(activeSlide = null) {
      this.rebuildLoop(activeSlide);
    }

    scheduleLoopRebuild() {
      window.cancelAnimationFrame(this.loopRebuildFrame);
      this.loopRebuildFrame = window.requestAnimationFrame(() => this.rebuildLoop());
    }

    rebuildLoop(activeSlide = null) {
      const preservedSlideId = activeSlide?.id || this.realSliderItems?.[this.currentPage - 1]?.id;

      this.removeLoopClones();
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      const visibleRealSlides = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
      this.realSliderItems = visibleRealSlides;
      this.sliderFirstItemNode = visibleRealSlides[0];

      if (!this.loopMediaQuery.matches || visibleRealSlides.length < 2) {
        this.loopActive = false;
        super.initPages();
        return;
      }

      const firstSlide = visibleRealSlides[0];
      const lastSlide = visibleRealSlides[visibleRealSlides.length - 1];
      firstSlide.setAttribute('data-slider-loop-real-first', '');
      this.classList.add('pdp-slider--looping');

      this.loopLeadingClone = this.createLoopClone(lastSlide, 'leading');
      this.loopTrailingClone = this.createLoopClone(firstSlide, 'trailing');
      this.slider.insertBefore(this.loopLeadingClone, firstSlide);
      this.slider.append(this.loopTrailingClone);

      this.loopActive = true;
      this.sliderItemsToShow = visibleRealSlides;
      this.sliderItemOffset = visibleRealSlides[1].offsetLeft - visibleRealSlides[0].offsetLeft;
      this.slidesPerPage = 1;
      this.totalPages = visibleRealSlides.length;

      const preservedSlide = visibleRealSlides.find((slide) => slide.id === preservedSlideId) || firstSlide;
      this.currentPage = visibleRealSlides.indexOf(preservedSlide) + 1;
      this.jumpToSlide(preservedSlide);
      this.updateLoopControls();
    }

    removeLoopClones() {
      this.querySelectorAll('[data-slider-loop-clone]').forEach((clone) => clone.remove());
      this.querySelectorAll('[data-slider-loop-real-first]').forEach((slide) => {
        slide.removeAttribute('data-slider-loop-real-first');
      });
      this.classList.remove('pdp-slider--looping');
      this.loopLeadingClone = null;
      this.loopTrailingClone = null;
    }

    createLoopClone(sourceSlide, position) {
      const clone = sourceSlide.cloneNode(true);
      const idMap = new Map();
      const elementsWithIds = [clone, ...clone.querySelectorAll('[id]')];

      elementsWithIds.forEach((element, index) => {
        if (!element.id) return;
        const originalId = element.id;
        const cloneId = `LoopClone-${position}-${originalId}-${index}`;
        idMap.set(originalId, cloneId);
        element.id = cloneId;
      });

      const idReferenceAttributes = ['aria-controls', 'aria-describedby', 'aria-labelledby', 'aria-owns', 'for'];
      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        idReferenceAttributes.forEach((attribute) => {
          if (!element.hasAttribute(attribute)) return;
          const references = element.getAttribute(attribute).split(/\s+/).map((id) => idMap.get(id) || id);
          element.setAttribute(attribute, references.join(' '));
        });

        const href = element.getAttribute('href');
        if (href?.startsWith('#') && idMap.has(href.slice(1))) {
          element.setAttribute('href', `#${idMap.get(href.slice(1))}`);
        }

        element.removeAttribute('data-media-id');
        element.removeAttribute('aria-current');
      });

      clone.dataset.sliderLoopClone = position;
      clone.dataset.sliderLoopSource = sourceSlide.id;
      clone.classList.remove('is-active');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');

      return clone;
    }

    update() {
      if (!this.loopSetupComplete || !this.loopActive) {
        super.update();
        return;
      }
      if (this.isJumping || !this.realSliderItems?.length) return;

      const previousPage = this.currentPage;
      const closestSlide = [this.loopLeadingClone, ...this.realSliderItems, this.loopTrailingClone]
        .reduce((closest, slide) => {
          return Math.abs(slide.offsetLeft - this.slider.scrollLeft) < Math.abs(closest.offsetLeft - this.slider.scrollLeft)
            ? slide
            : closest;
        });

      let realSlideIndex = this.realSliderItems.indexOf(closestSlide);
      if (closestSlide === this.loopLeadingClone) realSlideIndex = this.realSliderItems.length - 1;
      if (closestSlide === this.loopTrailingClone) realSlideIndex = 0;

      this.currentPage = realSlideIndex + 1;
      this.updateLoopControls();

      if (this.currentPage !== previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
          currentPage: this.currentPage,
          currentElement: this.realSliderItems[realSlideIndex]
        }}));
      }

      if (!this.supportsScrollEnd) {
        window.clearTimeout(this.loopResetTimer);
        this.loopResetTimer = window.setTimeout(() => this.resetLoopBoundary(), 180);
      }
    }

    updateLoopControls() {
      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      this.sliderControlButtons.forEach((button, index) => {
        const isCurrent = index === this.currentPage - 1;
        button.classList.toggle('slider-counter__link--active', isCurrent);
        if (isCurrent) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      this.prevButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');
    }

    resetLoopBoundary() {
      if (!this.loopActive || this.isJumping) return;

      const leadingBoundary = (this.loopLeadingClone.offsetLeft + this.realSliderItems[0].offsetLeft) / 2;
      const trailingBoundary = (
        this.realSliderItems[this.realSliderItems.length - 1].offsetLeft + this.loopTrailingClone.offsetLeft
      ) / 2;

      if (this.slider.scrollLeft < leadingBoundary) {
        this.jumpToSlide(this.realSliderItems[this.realSliderItems.length - 1]);
      } else if (this.slider.scrollLeft > trailingBoundary) {
        this.jumpToSlide(this.realSliderItems[0]);
      }
    }

    jumpToSlide(slide) {
      if (!slide) return;

      this.isJumping = true;
      const scrollBehavior = this.slider.style.scrollBehavior;
      const scrollSnapType = this.slider.style.scrollSnapType;
      this.slider.style.scrollBehavior = 'auto';
      this.slider.style.scrollSnapType = 'none';
      this.slider.scrollLeft = slide.offsetLeft;

      window.requestAnimationFrame(() => {
        this.slider.style.scrollBehavior = scrollBehavior;
        this.slider.style.scrollSnapType = scrollSnapType;
        this.isJumping = false;
      });
    }

    onButtonClick(event) {
      if (!this.loopActive) {
        super.onButtonClick(event);
        return;
      }

      event.preventDefault();
      const isNext = event.currentTarget.name === 'next';
      const currentIndex = this.currentPage - 1;
      let targetSlide = this.realSliderItems[currentIndex + (isNext ? 1 : -1)];

      if (!targetSlide && isNext) targetSlide = this.loopTrailingClone;
      if (!targetSlide && !isNext) targetSlide = this.loopLeadingClone;
      this.slider.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
    }

    linkToSlide(event) {
      if (!this.loopActive) {
        super.linkToSlide(event);
        return;
      }

      event.preventDefault();
      const targetSlide = this.realSliderItems[this.sliderControlButtons.indexOf(event.currentTarget)];
      if (targetSlide) this.slider.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
    }
  }

  customElements.define('pdp-slider-component', PDPProductSlider);
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages(activeMedia);
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
